import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper, CircularProgress, Alert as MuiAlert, Chip, Button, Tooltip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, useMediaQuery } from '@mui/material';
import { TrendingUp, Assignment, Business, Assessment, Warning, CheckCircle, ErrorOutline, InfoOutlined, Refresh as RefreshIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import analyticsService, { KPIsResponse, RecentAudit, PeriodTrend, Distribution, Alert, BusinessRanking, BusinessEvolution, CategoryComparison } from '../../services/analyticsService';
import TrendChart from '../../components/Charts/TrendChart';
import RankingChart from '../../components/Charts/RankingChart';
import DistributionChart from '../../components/Charts/DistributionChart';
import BusinessEvolutionChart from '../../components/Charts/BusinessEvolutionChart';
import CategoryComparisonChart from '../../components/Charts/CategoryComparisonChart';
import EnhancedStatCard from '../../components/Cards/EnhancedStatCard';
import { formatDate } from '../../utils/dateUtils';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [kpis, setKpis] = useState<KPIsResponse | null>(null);
  const [recentAudits, setRecentAudits] = useState<RecentAudit[]>([]);
  const [trends, setTrends] = useState<PeriodTrend[]>([]);
  const [distribution, setDistribution] = useState<Distribution[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [rankings, setRankings] = useState<BusinessRanking[]>([]);
  const [businessEvolution, setBusinessEvolution] = useState<BusinessEvolution[]>([]);
  const [categoryComparison, setCategoryComparison] = useState<CategoryComparison[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const isAdmin = user?.role === 'admin';
      const isManager = user?.role === 'manager';
      const [kpisData, auditsData, trendsData, distributionData, alertsData, rankingsData, evolutionData, comparisonData] = await Promise.all([
        analyticsService.getKPIs(),
        analyticsService.getRecentAudits(5),
        !isManager ? analyticsService.getTrends({ type: 'general' }) : Promise.resolve([]),
        analyticsService.getDistribution(),  // todos los roles (backend filtra por negocio asignado)
        analyticsService.getAlerts(),
        isAdmin
          ? analyticsService.getRankings({ type: 'businesses', limit: 5, order: 'DESC' })
          : Promise.resolve([]),
        isManager ? analyticsService.getBusinessEvolution() : Promise.resolve([]),
        isManager ? analyticsService.getCategoryComparison() : Promise.resolve([]),
      ]);
      setKpis(kpisData);
      setRecentAudits(auditsData);
      setTrends(trendsData || []);
      setDistribution(distributionData);
      setAlerts(alertsData);
      if (isAdmin) {
        setRankings(rankingsData as BusinessRanking[]);
      }
      setBusinessEvolution(evolutionData as BusinessEvolution[]);
      setCategoryComparison(comparisonData as CategoryComparison[]);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('Error cargando dashboard:', err);
      setError(err.message || 'Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const getRoleSpecificStats = () => {
    if (!kpis) return [];
    const adminKpis: any = kpis.kpis;
    const trendData: any = kpis.trends || {};
    
    if (user?.role === 'admin') {
      const avgCompliance = adminKpis.avg_compliance ? parseFloat(adminKpis.avg_compliance) : 0;
      return [
        {
          title: 'Total Negocios',
          value: adminKpis.total_businesses || '0',
          icon: <Business />,
          color: '#1976d2',
          trend: `${adminKpis.total_auditors || '0'} auditores activos`,
        },
        {
          title: 'Auditorías Completadas',
          value: adminKpis.completed_audits || '0',
          icon: <Assessment />,
          color: '#2e7d32',
          trend: `${trendData.audits_month_actual || '0'} este mes`,
        },
        {
          title: 'Promedio General',
          value: avgCompliance ? avgCompliance.toFixed(1) + '%' : '0%',
          icon: <TrendingUp />,
          color: '#ed6c02',
          isPercentage: true,
          percentageValue: avgCompliance || 0,
        },
        {
          title: 'Auditorías Borrador',
          value: adminKpis.draft_audits || '0',
          icon: <Warning />,
          color: '#d32f2f',
        },
      ];
    } else if (user?.role === 'auditor') {
      const miPromedio = adminKpis.my_average ? parseFloat(adminKpis.my_average) : 0;
      return [
        {
          title: 'Mis Auditorías',
          value: adminKpis.my_audits || '0',
          icon: <Assignment />,
          color: '#1976d2',
          trend: `${adminKpis.audits_week || '0'} esta semana`,
        },
        {
          title: 'Mi Promedio',
          value: miPromedio ? miPromedio.toFixed(1) + '%' : '0%',
          icon: <TrendingUp />,
          color: '#2e7d32',
          isPercentage: true,
          percentageValue: miPromedio || 0,
        },
        {
          title: 'Negocios Asignados',
          value: adminKpis.assigned_businesses || '0',
          icon: <Business />,
          color: '#ed6c02',
        },
        {
          title: 'Completadas',
          value: adminKpis.completadas || '0',
          icon: <CheckCircle />,
          color: '#2e7d32',
        },
      ];
    } else {
      const lastAuditCompliance = adminKpis.ultima_auditoria_cumplimiento ? parseFloat(adminKpis.ultima_auditoria_cumplimiento) : null;
      const promedioTrimestre = adminKpis.promedio_trimestre ? parseFloat(adminKpis.promedio_trimestre) : null;
      const avgCompliance = adminKpis.avg_compliance ? parseFloat(adminKpis.avg_compliance) : null;
      return [
        {
          title: 'Total Auditorías',
          value: adminKpis.total_audits || '0',
          icon: <Assessment />,
          color: '#1976d2',
        },
        {
          title: 'Última Auditoría',
          value: lastAuditCompliance ? lastAuditCompliance.toFixed(1) + '%' : '0%',
          icon: <CheckCircle />,
          color: '#2e7d32',
          isPercentage: true,
          percentageValue: lastAuditCompliance || 0,
        },
        {
          title: 'Promedio General',
          value: avgCompliance ? avgCompliance.toFixed(1) + '%' : '0%',
          icon: <TrendingUp />,
          color: '#ed6c02',
          isPercentage: true,
          percentageValue: avgCompliance || 0,
        },
        {
          title: 'Promedio Trimestre',
          value: promedioTrimestre ? promedioTrimestre.toFixed(1) + '%' : '0%',
          icon: <TrendingUp />,
          color: '#1976d2',
          isPercentage: true,
          percentageValue: promedioTrimestre || 0,
        },
      ];
    }
  };

  const getAlertIcon = (type: string) => type === 'error' ? <ErrorOutline /> : type === 'warning' ? <Warning /> : <InfoOutlined />;
  const getAlertSeverity = (type: string): 'error' | 'warning' | 'info' | 'success' => type as 'error' | 'warning' | 'info' | 'success';

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh"><CircularProgress /></Box>;
  if (error) return <Box p={3}><MuiAlert severity="error" onClose={() => setError(null)}>{error}</MuiAlert></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h4" gutterBottom>{getWelcomeMessage()}, {user?.name}</Typography>
            <Typography variant="body1" color="textSecondary">Aquí tienes un resumen de tu actividad</Typography>
          </Box>
          <Tooltip title={lastUpdated ? `Último actualizado: ${lastUpdated.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}` : 'Actualizar datos'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadDashboardData}
                disabled={loading}
                sx={{ mt: 1 }}
              >
                {lastUpdated
                  ? `Actualizado ${lastUpdated.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`
                  : 'Actualizar'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Grid container spacing={3} mb={4}>
        {getRoleSpecificStats().map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <EnhancedStatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      {/* Acceso rápido para manager */}
      {user?.role === 'manager' && (
        <Box mb={4}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Assessment />}
            onClick={() => navigate('/audits')}
            sx={{ bgcolor: '#000', color: '#FFEB5D', '&:hover': { bgcolor: '#222' } }}
          >
            Ver mis auditorías
          </Button>
        </Box>
      )}

      {/* ── Alertas ── */}
      {alerts.length > 0 && (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom fontWeight={700}>Alertas y Notificaciones</Typography>
          <Grid container spacing={2}>
            {alerts.map((alert, index) => (
              <Grid item xs={12} key={index}>
                <MuiAlert severity={getAlertSeverity(alert.type)} icon={getAlertIcon(alert.type)}>
                  <Typography variant="body2">{alert.message}</Typography>
                  {alert.details && Array.isArray(alert.details) && alert.details.length > 0 && (
                    <Box mt={1}>
                      {alert.details.map((detail, idx) => (
                        <Chip 
                          key={idx} 
                          label={`${detail.name}: ${parseFloat(detail.average).toFixed(1)}%`} 
                          size="small" 
                          color={parseFloat(detail.average) < 60 ? 'error' : parseFloat(detail.average) < 80 ? 'warning' : 'success'}
                          sx={{ mr: 1, mt: 0.5 }} 
                        />
                      ))}
                    </Box>
                  )}
                </MuiAlert>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ── Gráficos Manager ── */}
      {user?.role === 'manager' && (
        <Grid container spacing={3} mb={4}>
          {distribution.length > 0 && (
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{ p: 3, height: '100%', minHeight: 400, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
              >
                <DistributionChart data={distribution} title="Estado de Auditorías" />
              </Paper>
            </Grid>
          )}
          <Grid item xs={12} md={distribution.length > 0 ? 8 : 12}>
            <Paper
              elevation={0}
              sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, minHeight: 400 }}
            >
              <BusinessEvolutionChart data={businessEvolution} title="Desempeño por Formulario y Negocio" />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, minHeight: 400 }}
            >
              <CategoryComparisonChart data={categoryComparison} title="Desempeño por Categoría" />
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── Gráficos Admin / Auditor ── */}
      {user?.role !== 'manager' && (
        <Grid container spacing={3} mb={4}>
          {trends.length > 0 && (
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 3, height: '100%', minHeight: 480, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <TrendChart data={trends} title="Tendencia de Cumplimiento (Últimos 30 días)" type="area" />
              </Paper>
            </Grid>
          )}
          {distribution.length > 0 && (
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, height: '100%', minHeight: 480, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <DistributionChart data={distribution} title="Distribución por Estado" />
              </Paper>
            </Grid>
          )}
          {user?.role === 'admin' && rankings.length > 0 && (
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <RankingChart data={rankings} title="Top 5 Negocios por Cumplimiento" dataKey="avg_compliance" nameKey="name" />
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
      {recentAudits.length > 0 && (
        <Box>
          <Paper elevation={2} sx={{ p: isMobile ? 2 : 3, mb: 3, width: '100%', overflow: 'hidden' }}>
            <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Auditorías Recientes ({recentAudits.length})
            </Typography>

            {isMobile && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Desliza horizontalmente para ver todas las columnas →
              </Typography>
            )}

            <TableContainer
              sx={{
                '&::-webkit-scrollbar': { height: 8, width: 8 },
                '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 4 },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: 4,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.3)' },
                },
              }}
            >
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 90 : 130 }}>Negocio</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 90 : 140 }}>Formulario</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 70 : 110 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 70 : 110 }}>Auditor</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 60 : 80 }}>Puntaje</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 60 : 80 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentAudits.map((audit) => {
                    const score = audit.compliance_percentage ? parseFloat(audit.compliance_percentage) : null;
                    const scoreSx = score !== null
                      ? score >= 80 ? 'success.main' : score >= 60 ? 'warning.main' : 'error.main'
                      : 'text.secondary';
                    return (
                      <TableRow key={audit.id} hover>
                        <TableCell sx={{ fontSize: isMobile ? 10 : 14, fontWeight: 600 }}>{audit.business_name}</TableCell>
                        <TableCell sx={{ fontSize: isMobile ? 10 : 14 }}>{audit.form_name}</TableCell>
                        <TableCell sx={{ fontSize: isMobile ? 10 : 14 }}>{formatDate(audit.start_date, audit.business_timezone)}</TableCell>
                        <TableCell sx={{ fontSize: isMobile ? 10 : 14 }}>{audit.auditor_name}</TableCell>
                        <TableCell align="center">
                          {score !== null ? (
                            <Typography sx={{ fontWeight: 700, color: scoreSx, fontSize: isMobile ? 11 : 14 }}>
                              {score.toFixed(1)}%
                            </Typography>
                          ) : (
                            <Chip label="En progreso" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver auditoría">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => window.open(`/audits/${audit.id}`, '_blank')}
                            >
                              <VisibilityIcon fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
